import React, { Fragment, useEffect } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { TransformedData } from './modals/InventoryCheckResultModal'
import { useProductOrigins } from '../../hooks'
import {
  ExpiredColorStatus,
  getExpiredStatusToday,
  getStyleRow
} from '../../utils/colors'

interface InventoryCheckDynamicCycleTableProps {
  data: TransformedData[]
}

const useStyles = makeStyles({
  noWrap: {
    whiteSpace: 'nowrap',
    textAlign: 'center' // Centra el texto
  },
  centerText: {
    textAlign: 'center' // Centra el texto en las celdas del cuerpo
  }
})

export const InventoryCheckDynamicCycleTable: React.FC<
  InventoryCheckDynamicCycleTableProps
> = ({ data }) => {
  // Get all unique cycle numbers for dynamic column generation
  const allCycles = Array.from(
    new Set(data.flatMap((item) => Object.keys(item.cycles)))
  ).sort()

  // Obtenemos productOrigin
  const {
    productOriginsState: { productOrigins },
    getProductOrigins
  } = useProductOrigins()

  useEffect(() => {
    if (productOrigins?.length === 0) {
      getProductOrigins()
    }
  }, [])

  // Determinar el estilo de fondo basado en fecha de expiración
  const getBackgroundStyle = (
    expirationDate: string | undefined,
    skuProductOriginDays: number
  ) => {
    if (expirationDate) {
      return getStyleRow(
        getExpiredStatusToday(expirationDate, skuProductOriginDays)
      )
    }
    return { backgroundColor: '#f5f5f5', borderBottom: '1px solid #ccc' } // Gris por defecto
  }

  // Determinar el color de texto basado en fecha de expiración
  const getTextColor = (
    expirationDate: string | undefined,
    skuProductOriginDays: number
  ) => {
    if (expirationDate) {
      const status = getExpiredStatusToday(expirationDate, skuProductOriginDays)
      if (status === ExpiredColorStatus.expired) {
        return '#c62828' // Rojo oscuro para fondo rojo pastel
      }
      if (status === ExpiredColorStatus.expireSoon) {
        return '#e79f31' // Naranja oscuro para fondo naranja pastel
      }
    }
    return '#5C8E2F' // Verde por defecto
  }

  const classes = useStyles()

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          {/* Header Row */}
          <TableRow>
            <TableCell rowSpan={2}>Posición</TableCell>
            <TableCell rowSpan={2}>SKU</TableCell>
            <TableCell rowSpan={2}>EAN</TableCell>
            <TableCell rowSpan={2}>Lote/Fecha</TableCell>
            {allCycles.map((cycle) => (
              <TableCell key={cycle} colSpan={4} align='center'>
                Conteo {cycle}
              </TableCell>
            ))}
          </TableRow>
          {/* Sub-Headers for User, Real, Checked, Difference */}
          <TableRow>
            {allCycles.map(() => (
              <Fragment>
                <TableCell className={classes.noWrap}>Usuario</TableCell>
                <TableCell className={classes.noWrap}>Cant. Sistema</TableCell>
                <TableCell className={classes.noWrap}>
                  Cant. Encontrada
                </TableCell>
                <TableCell className={classes.noWrap}>Resultado</TableCell>
              </Fragment>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => {
            // ✅ Aquí puedes usar código normal de JS
            const skuProductOriginDays =
              productOrigins?.find(
                (el) => el.productOriginId === row.productOriginId
              )?.expirationDays || 0

            return (
              <TableRow
                key={`${row.position}_${index}`}
                style={{
                  ...getBackgroundStyle(
                    row?.expirationDate || undefined,
                    skuProductOriginDays
                  ),
                  fontSize: '0.8rem',
                  color: getTextColor(
                    row?.expirationDate || undefined,
                    skuProductOriginDays
                  )
                }}
              >
                {/* Position and SKU */}
                <TableCell
                  style={{
                    color: getTextColor(
                      row?.expirationDate || undefined,
                      skuProductOriginDays
                    )
                  }}
                >
                  {row.position}
                </TableCell>
                <TableCell
                  style={{
                    color: getTextColor(
                      row?.expirationDate || undefined,
                      skuProductOriginDays
                    )
                  }}
                >
                  {row.sku + ' -  ' + skuProductOriginDays}
                </TableCell>
                <TableCell
                  style={{
                    color: getTextColor(
                      row?.expirationDate || undefined,
                      skuProductOriginDays
                    )
                  }}
                >
                  {row.ean + ' -  ' + skuProductOriginDays}
                </TableCell>
                <TableCell
                  style={{
                    color: getTextColor(
                      row?.expirationDate || undefined,
                      skuProductOriginDays
                    )
                  }}
                >
                  {row.batch}
                </TableCell>

                {/* Cycles Data */}
                {allCycles.map((cycle) => {
                  const cycleData = row.cycles[cycle]
                  return cycleData ? (
                    <Fragment key={cycle}>
                      <TableCell className={classes.centerText}>
                        {cycleData.User || 'N/A'}
                      </TableCell>
                      <TableCell className={classes.centerText}>
                        {cycleData.RealQty}
                      </TableCell>
                      <TableCell className={classes.centerText}>
                        {cycleData.CheckedQty}
                      </TableCell>
                      <TableCell className={classes.centerText}>
                        {cycleData.CheckedQty - cycleData.RealQty}
                      </TableCell>
                    </Fragment>
                  ) : (
                    <Fragment key={cycle}>
                      <TableCell colSpan={4} align='center'>
                        Conteo {cycle} - Item no registrado
                      </TableCell>
                    </Fragment>
                  )
                })}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
